import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atom, getDefaultStore } from 'jotai/vanilla';
import { queryClientAtom } from 'jotai-tanstack-query';
import capitalize from 'lodash/capitalize';

import { User } from '@cast/types';
import { isE2E } from '@cast/utils';

import { getSessionQueryOptions } from './api/useSessionQuery';
import { getUserProfileQueryOptions } from './api/useUserProfileQuery';

const failureMessage = 'Failed to authorize';

const userAtom = atom<Promise<User>>(async (get) => {
  const queryClient = get(queryClientAtom);
  try {
    const session = await queryClient.fetchQuery(getSessionQueryOptions());
    const userProfile = await queryClient.fetchQuery(
      getUserProfileQueryOptions({ userId: session.userId })
    );
    return {
      id: session.userId,
      guid: userProfile.id,
      firstName: capitalize(session.nickname),
      email: session.email,
      emailHash: userProfile.emailHash,
      firstLogin: !!userProfile.firstLogin,
      name: userProfile.name,
      readmeIoJwt: userProfile.readmeIoJwt,
      fingerprint: userProfile.fingerprint,
    };
  } catch {
    throw new Error(failureMessage);
  }
});

const userSyncAtom = unwrap(userAtom);

export const getUser = (): User | undefined => {
  return getDefaultStore().get(userSyncAtom);
};

export const useUser = () => useAtomValue(userAtom);

export const isFailedToAuthorizeError = (error: Error) => {
  return error.message.includes(failureMessage);
};

export const shouldEnableFingerprint = (user: User) => {
  return (
    !!window.env.VITE_FINGERPRINT_PUBLIC_KEY &&
    !!window.env.VITE_FINGERPRINT_PROXY_URL &&
    !isE2E() &&
    user.firstLogin &&
    !user.fingerprint
  );
};

export const InitAuth = () => {
  useUser();
  return null;
};
